import { useEffect, useMemo, useState } from "react";
import { PageHero, Container } from "../components/Section";
import { api } from "../lib/api";
import { MapPin, Clock } from "lucide-react";
import { motion } from "framer-motion";

const CATEGORIES = [
  { key: "all", label: "All Events" },
  { key: "services", label: "Services" },
  { key: "bible-study", label: "Bible Studies" },
  { key: "youth", label: "Youth" },
  { key: "ministry", label: "Ministry" },
  { key: "special", label: "Special" },
];

// Today's date as YYYY-MM-DD in LOCAL time (avoids UTC-shift bugs)
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Parse "YYYY-MM-DD" without timezone shift
function parseDate(str) {
  const parts = (str || "").split("-");
  if (parts.length !== 3) return null;
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("upcoming"); // upcoming | past

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const today = todayISO();

  const upcomingCount = useMemo(() => events.filter((e) => e.event_date >= today).length, [events, today]);
  const pastCount = useMemo(() => events.filter((e) => e.event_date < today).length, [events, today]);

  const visible = useMemo(() => {
    let list = events.filter((e) => timeFilter === "past" ? e.event_date < today : e.event_date >= today);
    if (filter !== "all") list = list.filter((e) => e.category === filter);
    // Upcoming sorted asc (nearest first), past sorted desc (most recent first)
    list.sort((a, b) => timeFilter === "past"
      ? b.event_date.localeCompare(a.event_date)
      : a.event_date.localeCompare(b.event_date)
    );
    return list;
  }, [events, filter, timeFilter, today]);

  return (
    <div data-testid="calendar-page">
      <PageHero
        overline="Church Calendar"
        title={<>What's <em className="text-gold-light italic font-light">happening</em> at Sixth Mount Zion.</>}
        kicker="Worship, study, fellowship, and outreach — all on one page. Find your next gathering."
        image="/images/branding/sanctuary-wide.jpg"
      />

      <Container className="pb-32">
        {/* Upcoming vs Past tabs */}
        <div className="flex gap-2 border-b border-white/10 mb-8" data-testid="calendar-time-tabs">
          <TabBtn
            active={timeFilter === "upcoming"}
            onClick={() => setTimeFilter("upcoming")}
            testId="calendar-tab-upcoming"
          >
            Upcoming <Count>{upcomingCount}</Count>
          </TabBtn>
          <TabBtn
            active={timeFilter === "past"}
            onClick={() => setTimeFilter("past")}
            testId="calendar-tab-past"
          >
            Past Events <Count>{pastCount}</Count>
          </TabBtn>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              className={`px-6 py-2.5 text-xs uppercase tracking-widest rounded-full transition-all font-bold ${
                filter === c.key ? "brand-gradient text-white shadow-lg shadow-magenta/30" : "border border-white/20 text-white/70 hover:bg-white/5 hover:border-gold/40"
              }`}
              data-testid={`calendar-filter-${c.key}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className={`space-y-px bg-white/5 ${timeFilter === "past" ? "opacity-80" : ""}`}>
          {visible.length === 0 && (
            <div className="bg-wine-950 p-12 text-white/50 italic font-serif" data-testid="calendar-empty">
              {timeFilter === "past" ? "No past events in this category." : "No upcoming events in this category yet."}
            </div>
          )}
          {visible.map((e, i) => {
            const d = parseDate(e.event_date);
            const isPast = timeFilter === "past";
            return (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className={`bg-wine-950 hover:bg-wine-900 transition-colors p-8 grid md:grid-cols-12 gap-6 items-center ${isPast ? "grayscale-[0.35]" : ""}`}
                data-testid={`event-row-${e.id}`}
              >
                <div className="md:col-span-2">
                  <div className={`font-serif text-5xl font-black leading-none ${isPast ? "text-white/50" : "text-gold"}`}>
                    {d ? d.getDate() : "—"}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-white/50 mt-2">
                    {d ? d.toLocaleDateString("en-US", { month: "short", year: "numeric" }) : ""}
                  </div>
                </div>
                <div className="md:col-span-7">
                  <div className="text-xs uppercase tracking-widest text-gold/70 mb-2 flex items-center gap-2">
                    {e.category}
                    {isPast && <span className="text-white/40 text-[10px]">· archived</span>}
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl font-bold mb-2">{e.title}</h3>
                  <p className="text-white/65 leading-relaxed line-clamp-3">{e.description}</p>
                </div>
                <div className="md:col-span-3 space-y-2 text-sm text-white/70">
                  {e.start_time && <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold/60" /> {e.start_time}{e.end_time ? ` – ${e.end_time}` : ""}</div>}
                  {e.location && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold/60" /> {e.location}</div>}
                </div>
              </motion.div>
            );
          })}
        </div>
      </Container>
    </div>
  );
}

function TabBtn({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-6 py-3 text-sm uppercase tracking-widest font-bold transition-colors -mb-px border-b-2 flex items-center gap-2 ${
        active ? "text-gold border-gold" : "text-white/55 border-transparent hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function Count({ children }) {
  return (
    <span className="text-xs bg-white/10 px-2 py-0.5 rounded-full font-mono">{children}</span>
  );
}
